<script setup lang="ts">
import { computed, ref, provide } from 'vue'
import { RouterView, RouterLink, useRoute } from 'vue-router'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import Menubar from 'primevue/menubar'
import Menu from 'primevue/menu'
import Avatar from 'primevue/avatar'
import Button from 'primevue/button'
import PromptDialog from '@/components/shared/PromptDialog.vue'
import { PromptDialogKey } from '@/composables/useConfirmModal'
import type { PromptTextFn } from '@/composables/useConfirmModal'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useTheme, type ThemePreference } from '@/composables/useTheme'
import logoImg from '@/assets/images/logo.png'
import gtgpImg from '@/assets/images/gtgp.png'

const promptDialogRef = ref<InstanceType<typeof PromptDialog> | null>(null)

const openPrompt: PromptTextFn = (opts) => {
  return promptDialogRef.value!.open(opts)
}
provide(PromptDialogKey, openPrompt)

const authStore = useAuthStore()
const userStore = useUserStore()
const { preference: themePreference, setTheme } = useTheme()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const userName = computed(() => userStore.userName || authStore.account?.name || '')
const userInitials = computed(() => {
  const parts = userName.value.trim().split(/\s+/)
  if (parts.length >= 2) return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase()
  return userName.value.slice(0, 2).toUpperCase()
})
const isAdmin = computed(() => userStore.isAdmin)

const userMenuRef = ref<InstanceType<typeof Menu> | null>(null)

function toggleUserMenu(event: Event) {
  userMenuRef.value?.toggle(event)
}

const themeChoices: { key: ThemePreference; label: string; icon: string }[] = [
  { key: 'light', label: 'Light', icon: 'pi pi-sun' },
  { key: 'dark', label: 'Dark', icon: 'pi pi-moon' },
  { key: 'system', label: 'System', icon: 'pi pi-desktop' },
]

const userMenuItems = computed(() => [
  ...themeChoices.map((t) => ({
    label: t.label,
    icon: t.icon,
    class: themePreference.value === t.key ? 'font-bold' : '',
    data: { active: themePreference.value === t.key },
    command: () => setTheme(t.key),
  })),
  { separator: true },
  {
    label: 'Logout',
    icon: 'pi pi-sign-out',
    command: () => handleLogout(),
  },
])

const navItems = computed(() => {
  const items: { label: string; icon: string; route?: string; class?: string; disabled?: boolean; items?: { label: string; icon: string; route?: string; disabled?: boolean }[] }[] = [
    {
      label: 'Home',
      icon: 'pi pi-home',
      route: '/home',
    },
    {
      label: 'Your form',
      icon: 'pi pi-file-edit',
      items: [
        { label: 'Terroir', icon: 'pi pi-map-marker', route: '/forms/terroir' },
        { label: 'People & Governance', icon: 'pi pi-users', route: '/forms/general' },
        { label: 'Making', icon: 'pi pi-globe', route: '/forms/environment' },
        { label: 'Hosting', icon: 'pi pi-heart', route: '/forms/ais' },
        { label: 'Political Contribution', icon: 'pi pi-comments', route: '/forms/lobbying' },
      ],
    },
    {
      label: 'Your data',
      icon: 'pi pi-database',
      items: [
        { label: 'Export', icon: 'pi pi-download', route: '/exports' },
      ],
    },
  ]

  if (isAdmin.value) {
    items.push({
      label: 'Admin',
      icon: 'pi pi-cog',
      class: 'ml-auto',
      items: [
        { label: 'Users', icon: 'pi pi-user', route: '/manage/users' },
        { label: 'Entities', icon: 'pi pi-th-large', route: '/manage/entities' },
        { label: 'Legal entities', icon: 'pi pi-id-card', route: '/manage/legal-entities' },
        { label: 'Fiscal year', icon: 'pi pi-calendar', route: '/manage/timeframes' },
        { label: 'Currencies', icon: 'pi pi-money-bill', route: '/manage/currencies' },
        { label: 'Forms configuration', icon: 'pi pi-cog', route: '/manage/forms-config' },
      ],
    })
  }

  return items
})

const route = useRoute()

function isItemActive(item: Record<string, unknown>): boolean {
  if (typeof item.route === 'string' && route.path.startsWith(item.route)) return true
  if (Array.isArray(item.items)) return item.items.some((sub: Record<string, unknown>) => typeof sub.route === 'string' && route.path.startsWith(sub.route))
  return false
}

async function handleLogout() {
  await authStore.logout()
}
</script>

<template>
  <div id="app">
    <template v-if="isAuthenticated">
      <a href="#main-content" class="sr-skip-link">Skip to main content</a>
      <Menubar :model="navItems" class="app-navbar" breakpoint="960px">
        <template #start>
          <div class="flex align-items-center gap-3 mr-4">
            <RouterLink to="/home" class="flex align-items-center gap-3 no-underline" aria-label="Go to homepage">
              <img :src="logoImg" alt="Pernod Ricard" class="h-3rem img-dark-invert" />
              <img :src="gtgpImg" alt="Good Times From A Good Place" class="h-3rem img-dark-invert" />
            </RouterLink>
            <RouterLink to="/home" class="text-xl font-bold no-underline text-brand hidden md:inline">
              S&amp;R Reporting
            </RouterLink>
          </div>
        </template>
        <template #item="{ item, props, hasSubmenu, root }">
          <RouterLink
            v-if="item.route"
            :to="item.route"
            v-bind="props.action"
            :class="['flex align-items-center gap-2', isItemActive(item) ? 'text-primary font-bold nav-active-item' : '']"
          >
            <span :class="item.icon" />
            <span>{{ item.label }}</span>
          </RouterLink>
          <a
            v-else-if="item.disabled"
            v-bind="props.action"
            class="flex align-items-center gap-2 opacity-40 pointer-events-none"
            tabindex="-1"
            aria-disabled="true"
          >
            <span :class="item.icon" />
            <span>{{ item.label }}</span>
          </a>
          <a
            v-else
            v-bind="props.action"
            :class="['flex align-items-center gap-2', isItemActive(item) ? 'text-primary font-bold' : '']"
          >
            <span :class="item.icon" />
            <span>{{ item.label }}</span>
            <i v-if="hasSubmenu" :class="['pi', { 'pi-angle-down': root, 'pi-angle-right': !root }]" />
          </a>
        </template>
        <template #end>
          <Button
            text
            rounded
            aria-label="User menu"
            aria-haspopup="true"
            aria-controls="user-menu"
            class="flex align-items-center gap-2"
            @click="toggleUserMenu"
          >
            <Avatar :label="userInitials" shape="circle" />
            <span class="hidden md:inline text-color">{{ userName }}</span>
            <i class="pi pi-chevron-down text-xs text-color-secondary" aria-hidden="true" />
          </Button>
          <Menu id="user-menu" ref="userMenuRef" :model="userMenuItems" :popup="true" class="w-14rem">
            <template #start>
              <div class="px-3 pt-2 pb-1">
                <span class="text-sm font-semibold text-color-secondary">Theme</span>
              </div>
            </template>
            <template #item="{ item, props }">
              <a v-bind="props.action" class="flex align-items-center gap-2">
                <span :class="item.icon" />
                <span class="flex-1">{{ item.label }}</span>
                <i v-if="item.data?.active" class="pi pi-check text-primary" aria-hidden="true" />
              </a>
            </template>
          </Menu>
        </template>
      </Menubar>

      <main id="main-content">
        <RouterView />
      </main>
    </template>

    <template v-else>
      <main id="main-content">
        <RouterView />
      </main>
    </template>

    <Toast />
    <ConfirmDialog />
    <PromptDialog ref="promptDialogRef" />
  </div>
</template>
